/**
 * TypeScript port of pure weigh-in calculations.
 *
 * The backend (app/services/weigh_in_calc.py) is the canonical source; this
 * mirror exists so the form drawer can show live spread feedback while the
 * operator enters measurements. Keep the formulas in sync — sign convention
 * (Port = +Y, Stbd = -Y per PDF), constants, and the 0.1% spread gate all live here.
 */

import type { WingConfig } from "@/types/identifiers";

export const SPREAD_GATE = 0.001; // 0.1% — relative gate for weight + Mx
// TCG (My) is near-zero on a balanced boat, so relative spread is degenerate;
// gate transverse repeatability on an absolute TCG range instead (see ADR-041).
export const PLATFORM_TCG_TOLERANCE = 0.005; // m (5 mm)
export const PLATFORM_VCG = -0.5;

// Oil is entered in litres; mass = density × litres. LCG/TCG are fixed — oil
// always sits in the same place, so they are not operator-editable.
export const OIL_DENSITY_KG_PER_L = 0.88;
export const OIL_LCG_M = 7.7;
export const OIL_TCG_M = 0;

export interface PlatformStraps {
  stbd_aft_x: number;
  stbd_aft_y: number;
  port_aft_x: number;
  port_aft_y: number;
  stbd_fwd_x: number;
  stbd_fwd_y: number;
  port_fwd_x: number;
  port_fwd_y: number;
}

// Sign convention per `Single Boat Weight & Centres Calculation.pdf`:
// Port = +Y, Stbd = -Y (straps at ±3.74m).
export const F50_PLATFORM: PlatformStraps = {
  stbd_aft_x: 1.65,
  stbd_aft_y: -3.74,
  port_aft_x: 1.65,
  port_aft_y: 3.74,
  stbd_fwd_x: 11.55,
  stbd_fwd_y: -3.74,
  port_fwd_x: 11.55,
  port_fwd_y: 3.74,
};

export interface WingScales {
  head_x: number;
  head_z: number;
  clew_x: number;
  clew_z: number;
  butt_x: number;
  butt_z: number;
}

export const WING_SCALES: Partial<Record<WingConfig, WingScales>> = {
  // APW @ 5.1° rake — canonical config used for fleet equalization
  APW: { head_x: 2.1, head_z: 23.7, clew_x: 3.5, clew_z: 0.43, butt_x: 0, butt_z: 0 },
  // HAW / LAW: positions TBD — reference-only configs
};

export const WING_ACCESSORIES = {
  head_dicks_kg: 1.76,
  clew_dicks_kg: 1.85,
  butt_dicks_kg: 1.43,
  mhu_kg: 6.6,
  mhu_x: 2.1,
  mhu_z: 24.0,
  screen_kg: 18.2,
  screen_x: 1.0,
  screen_z: 0.7,
  // Wand sits at the masthead alongside the MHU (same arms).
  wand_kg: 0.8,
  wand_x: 2.1,
  wand_z: 24.0,
} as const;


// --------------------------------------------------------------------------
// Spread
// --------------------------------------------------------------------------

/** (max - min) / |mean| as a fraction. Returns 0 for empty or constant inputs. */
export function spread(values: number[]): number {
  if (values.length === 0) return 0;
  let min = values[0];
  let max = values[0];
  let sum = 0;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  const avg = sum / values.length;
  if (avg === 0) return min === max ? 0 : Infinity;
  return (max - min) / Math.abs(avg);
}

/**
 * Of >=3 derived set-results, the index whose removal brings the remaining sets
 * within the spread gate — i.e. the outlier worth excluding. Returns null if no
 * single removal clears the gate (or there are fewer than 3 sets). `other` is
 * my for platform, mz for wing.
 */
export function suggestOutlierIndex(
  sets: { weight: number; mx: number; other: number }[],
): number | null {
  if (sets.length < 3) return null;
  let best: { idx: number; max: number } | null = null;
  for (let i = 0; i < sets.length; i++) {
    const rest = sets.filter((_, j) => j !== i);
    const max = Math.max(
      spread(rest.map((s) => s.weight)),
      spread(rest.map((s) => s.mx)),
      spread(rest.map((s) => s.other)),
    );
    if (best === null || max < best.max) best = { idx: i, max };
  }
  return best && best.max <= SPREAD_GATE ? best.idx : null;
}

// --------------------------------------------------------------------------
// Platform
// --------------------------------------------------------------------------

export type PlatformReading = {
  stbd_aft_kg: number;
  port_aft_kg: number;
  stbd_fwd_kg: number;
  port_fwd_kg: number;
};

export interface PlatformSetResult {
  weight: number;
  mx: number;
  my: number;
}

export function computePlatformSet(
  r: PlatformReading,
  straps: PlatformStraps = F50_PLATFORM,
): PlatformSetResult {
  return {
    weight: r.stbd_aft_kg + r.port_aft_kg + r.stbd_fwd_kg + r.port_fwd_kg,
    mx:
      r.stbd_aft_kg * straps.stbd_aft_x +
      r.port_aft_kg * straps.port_aft_x +
      r.stbd_fwd_kg * straps.stbd_fwd_x +
      r.port_fwd_kg * straps.port_fwd_x,
    my:
      r.stbd_aft_kg * straps.stbd_aft_y +
      r.port_aft_kg * straps.port_aft_y +
      r.stbd_fwd_kg * straps.stbd_fwd_y +
      r.port_fwd_kg * straps.port_fwd_y,
  };
}

export interface SpreadReport {
  weight: number;
  mx: number;
  /** my for platform, mz for wing */
  other: number;
  /** Largest of the three spreads — gate passes when this is <= SPREAD_GATE. */
  maxSpread: number;
  passes: boolean;
}

export function platformSpread(
  readings: PlatformReading[],
  straps: PlatformStraps = F50_PLATFORM,
): SpreadReport {
  const sets = readings.map((r) => computePlatformSet(r, straps));
  const w = spread(sets.map((s) => s.weight));
  const mx = spread(sets.map((s) => s.mx));
  const my = spread(sets.map((s) => s.my));
  const maxSpread = Math.max(w, mx, my);
  return { weight: w, mx, other: my, maxSpread, passes: maxSpread <= SPREAD_GATE };
}

/** Absolute spread of TCG (= My / weight) across sets, in metres — the
 *  transverse repeatability check. Used instead of a relative My spread, which
 *  is degenerate because TCG's target is ~0 (see PLATFORM_TCG_TOLERANCE). */
export function platformTcgRange(
  readings: PlatformReading[],
  straps: PlatformStraps = F50_PLATFORM,
): number {
  const tcgs = readings
    .map((r) => computePlatformSet(r, straps))
    .filter((s) => s.weight !== 0)
    .map((s) => s.my / s.weight);
  if (tcgs.length === 0) return 0;
  return Math.max(...tcgs) - Math.min(...tcgs);
}

export interface PlatformResult {
  weight: number;
  mx: number;
  my: number;
  lcg: number;
  tcg: number;
}

/** A free-form mass booked against the platform weighing (oil's generalisation).
 *  `subtract` removes it (present at weigh); otherwise it is added. */
export interface PlatformItemInput {
  weightKg: number;
  lcg: number;
  tcg: number;
  subtract: boolean;
}

/** Signed mass for display, e.g. "−40.0 kg" (subtract) / "+12.5 kg" (add). */
export function formatSignedKg(weightKg: number, subtract: boolean): string {
  return `${subtract ? "−" : "+"}${weightKg.toFixed(1)} kg`;
}

/** Average the sets, then subtract oil and apply custom items. Pass
 *  corrector_snapshot through the backend when needed — for live drawer
 *  feedback we just show net-of-oil-and-items. */
export function computePlatform(
  readings: PlatformReading[],
  oilKg = 0,
  oilLcg = 0,
  oilTcg = 0,
  items: PlatformItemInput[] = [],
  straps: PlatformStraps = F50_PLATFORM,
): PlatformResult | null {
  if (readings.length === 0) return null;
  const sets = readings.map((r) => computePlatformSet(r, straps));
  const n = sets.length;
  const avgW = sets.reduce((a, s) => a + s.weight, 0) / n;
  const avgMx = sets.reduce((a, s) => a + s.mx, 0) / n;
  const avgMy = sets.reduce((a, s) => a + s.my, 0) / n;
  let weight = avgW - oilKg;
  let mx = avgMx - oilKg * oilLcg;
  let my = avgMy - oilKg * oilTcg;
  for (const it of items) {
    const s = it.subtract ? -it.weightKg : it.weightKg;
    weight += s;
    mx += s * it.lcg;
    my += s * it.tcg;
  }
  if (weight === 0) return null;
  return { weight, mx, my, lcg: mx / weight, tcg: my / weight };
}

// --------------------------------------------------------------------------
// Wing
// --------------------------------------------------------------------------

export type WingReading = {
  head_kg: number;
  clew_kg: number;
  butt_kg: number;
};

export interface WingToggles {
  mhu: boolean;
  screen: boolean;
  dicks: boolean;
  wand: boolean;
}

export interface WingSetResult {
  weight: number;
  mx: number;
  mz: number;
}

export function computeWingSet(
  r: WingReading,
  scales: WingScales,
  toggles: WingToggles,
): WingSetResult {
  let head = r.head_kg;
  let clew = r.clew_kg;
  let butt = r.butt_kg;

  if (toggles.dicks) {
    head -= WING_ACCESSORIES.head_dicks_kg;
    clew -= WING_ACCESSORIES.clew_dicks_kg;
    butt -= WING_ACCESSORIES.butt_dicks_kg;
  }

  let weight = head + clew + butt;
  let mx = head * scales.head_x + clew * scales.clew_x + butt * scales.butt_x;
  let mz = head * scales.head_z + clew * scales.clew_z + butt * scales.butt_z;

  if (toggles.mhu) {
    weight -= WING_ACCESSORIES.mhu_kg;
    mx -= WING_ACCESSORIES.mhu_kg * WING_ACCESSORIES.mhu_x;
    mz -= WING_ACCESSORIES.mhu_kg * WING_ACCESSORIES.mhu_z;
  }

  if (toggles.screen) {
    weight -= WING_ACCESSORIES.screen_kg;
    mx -= WING_ACCESSORIES.screen_kg * WING_ACCESSORIES.screen_x;
    mz -= WING_ACCESSORIES.screen_kg * WING_ACCESSORIES.screen_z;
  }

  if (toggles.wand) {
    weight -= WING_ACCESSORIES.wand_kg;
    mx -= WING_ACCESSORIES.wand_kg * WING_ACCESSORIES.wand_x;
    mz -= WING_ACCESSORIES.wand_kg * WING_ACCESSORIES.wand_z;
  }

  return { weight, mx, mz };
}

export function wingSpread(
  readings: WingReading[],
  scales: WingScales,
  toggles: WingToggles,
): SpreadReport {
  const sets = readings.map((r) => computeWingSet(r, scales, toggles));
  const w = spread(sets.map((s) => s.weight));
  const mx = spread(sets.map((s) => s.mx));
  const mz = spread(sets.map((s) => s.mz));
  const maxSpread = Math.max(w, mx, mz);
  return { weight: w, mx, other: mz, maxSpread, passes: maxSpread <= SPREAD_GATE };
}

export interface WingResult {
  weight: number;
  mx: number;
  mz: number;
  lcg: number;
  vcg: number;
}

export function computeWing(
  readings: WingReading[],
  scales: WingScales,
  toggles: WingToggles,
): WingResult | null {
  if (readings.length === 0) return null;
  const sets = readings.map((r) => computeWingSet(r, scales, toggles));
  const n = sets.length;
  const weight = sets.reduce((a, s) => a + s.weight, 0) / n;
  const mx = sets.reduce((a, s) => a + s.mx, 0) / n;
  const mz = sets.reduce((a, s) => a + s.mz, 0) / n;
  if (weight === 0) return null;
  return { weight, mx, mz, lcg: mx / weight, vcg: mz / weight };
}
